import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { getOverrides, useClasses } from '../../../utils/overrides';
import Text from '../../typography/Text';

import { createUseStyles } from '../../../utils/styles';
import styles from './styles';
const useStyles = createUseStyles(styles, 'Badge');

function Badge({
    classes: classesProp,
    children,
    overrides: overridesProp,
    className: classNameProp,
    variant,
    isActive,
    isTruncated,
    useTooltip,
    type = 'default',
    bold = false,
    medium = false,
    light = false,
    ...props
}) {
    const classes = useClasses(useStyles, classesProp);

    // Overrides
    const override = getOverrides(overridesProp, Badge.overrides);

    // Classes
    const rootClassName = useMemo(
        () =>
            classnames(
                classes.root,
                {
                    [classes[type]]: type,
                    [classes[variant]]: variant,
                    [classes.hasHover]: props.onClick,
                    [classes.isActive]: isActive,
                },
                classNameProp,
            ),
        [classNameProp, classes, isActive, props.onClick, type, variant],
    );

    const rootProps = {
        ...props,
        className: rootClassName,
    };

    return (
        <div {...rootProps} {...override.root}>
            <Text
                type="badges"
                className={classes.Text}
                {...override.Text}
                isTruncated={isTruncated}
                useTooltip={useTooltip}
                bold={bold}
                medium={medium}
                light={light}
            >
                {children}
            </Text>
        </div>
    );
}

Badge.overrides = ['root', 'Text'];

Badge.propTypes = {
    /** Content to be displayed inside the badge */
    children: PropTypes.node.isRequired,
    /** Custom CSS class for styling */
    className: PropTypes.string,
    /** Object with custom style overrides for inner elements */
    overrides: PropTypes.object,
    /** Visual style of the badge to indicate different states or categories */
    type: PropTypes.oneOf([
        'default',
        'outlined',
        'actionMinor',
        'semanticPositive',
        'semanticNegative',
        'semanticInfo',
        'semanticFocus',
        'caution',
        'ongoing',
        'promotion',
        // Legacy types for backward compatibility
        'error',
        'success',
        'info',
        'warning',
    ]),
    /** Alternative visual style that inverts the badge's colors */
    variant: PropTypes.oneOf(['inverted']),
    /** When true, applies active state styling to the badge */
    isActive: PropTypes.bool,
    /** When true, text that doesn't fit will be truncated with an ellipsis */
    isTruncated: PropTypes.bool,
    /** When true, displays the full text in a tooltip when hovering over a truncated badge */
    useTooltip: PropTypes.bool,
    /** Click handler function. When provided, the badge becomes interactive with hover effects */
    onClick: PropTypes.func,
    /** When true, displays text in bold (700) font weight */
    bold: PropTypes.bool,
    /** When true, displays text in medium (500) font weight */
    medium: PropTypes.bool,
    /** When true, displays text in light (300) font weight */
    light: PropTypes.bool,
};

export default React.memo(Badge);
